import { useKeycloakToken } from '../common/useKeycloak';
import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import { getDestinationsList } from '../../api/destinations/destinationsApi';
import DestinationsList from './DestinationsList';
import { Destination, DestinationType, Organization } from '../common/ObjectTypes';
import { ManageDestinationDetails } from './ManageDestinationDetails';
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { page_size } from '../../api/apiUtils';
import loadDestinationUrls from '../../api/destinations/loadDestinationUrls';
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import getUserPermissions from '../../api/users/getUserPermissions';
import { useKeycloak } from '@react-keycloak/web';
import { getEnvironment } from '../common/getEnvironment';
import { useHelp } from '../../context/HelpContext';
import { useTranslation } from 'react-i18next';

const DestinationsPage = () => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();

    useEffect(() => {
        setHelpContent({
            cards: [{
                title: t('Managing Destinations in Gundi'),
                description: t('Learn how to set up and configure destinations where your data gets sent.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/destinations' },
            }],
        });
    }, [i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);
    type FilterTypes = {
        base_url__in: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    };
    type FilterTags = {
        name: string,
        id: string
    };
    const defaultDestinationsFilter = {
        base_url__in: [],
        type__in: [],
        owner__in: [],
        ordering: "name",
        search: ""
    };
    const navigate = useNavigate();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const currentEnv = getEnvironment();
    const { keycloak } = useKeycloak();
    const [canAddDestination, setCanAddDestination] = useState(false);
    const [destinations, setDestinations] = useState<Destination[]>([]);
    const [destinationFilter, setDestinationFilter] = useState<FilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('destinationFilter');
        return savedFilter ? { ...defaultDestinationsFilter, ...JSON.parse(savedFilter) } : defaultDestinationsFilter;
    });
    const [totalCount, setTotalCount] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);
    const [destinationUrls, setDestinationUrls] = useState<any[]>([]);
    const [loadingDestinationUrls, setLoadingDestinationUrls] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [reloadDestinations, setReloadDestinations] = useState(true);
    const [destDetailsModal, setDestDetailsModal] = useState(false);

    const loadDestinationURlsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationUrls(true);
            try {
                let accumulated: string[] = [];
                let urlsOffset = 0;
                while (true) {
                    const [dest_urls, total, error] = await loadDestinationUrls(token, urlsOffset, "", accumulated);
                    if (error) break;
                    accumulated = dest_urls;
                    if (urlsOffset + page_size >= total) break;
                    urlsOffset += page_size;
                }
                setDestinationUrls([...new Set(accumulated)]);
            } finally {
                setLoadingDestinationUrls(false);
            }
        }
    }

    const loadDestinationTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationTypes(true);
            try {
                let accumulated: DestinationType[] = [];
                let typesOffset = 0;
                while (true) {
                    const [types, total, error] = await loadDestinationTypes(token, typesOffset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (typesOffset + page_size >= total) break;
                    typesOffset += page_size;
                }
                setDestinationTypes(accumulated);
            } finally {
                setLoadingDestinationTypes(false);
            }
        }
    }

    const loadOrganizationsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingOrganizations(true);
            try {
                let accumulated: Organization[] = [];
                let orgsOffset = 0;
                while (true) {
                    const [orgs, total, error] = await loadOrganizations(token, orgsOffset, "", accumulated);
                    if (error) break;
                    accumulated = orgs;
                    if (orgsOffset + page_size >= total) break;
                    orgsOffset += page_size;
                }
                setOrganizations(accumulated);
            } finally {
                setLoadingOrganizations(false);
            }
        }
    }

    useEffect(() => {
        sessionStorage.setItem('destinationFilter', JSON.stringify(destinationFilter));

        const loadPerms = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const temp = await getUserPermissions(token);
                    setCanAddDestination(temp);
                } catch (err) {
                    console.log(err);
                }
            }
        }

        const loadDestinations = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const destinationFilterQuery = {
                        base_url__in: destinationFilter.base_url__in.map(a => a.id).toString(),
                        type__in: destinationFilter.type__in.map(a => a.id).toString(),
                        owner__in: destinationFilter.owner__in.map(a => a.id).toString(),
                        ordering: destinationFilter.ordering,
                        search: destinationFilter.search,
                        offset: offset,
                        previous: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (offset - page_size),
                        next: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (offset + page_size)
                    }
                    if (destinationFilter.search !== '') {
                        ReactGA.event('destinations_search', {
                            search_term: destinationFilter.search || 'none',
                            environment: currentEnv
                        });
                    }
                    const temp = await getDestinationsList(token, destinationFilterQuery);
                    setDestinations(temp.results);
                    setTotalCount(temp.count);
                    setPageLoaded(true);
                    setReloadDestinations(false);
                } catch (err) {
                    console.log(err);
                }
            }
        }

        if (reloadDestinations) loadDestinations();
        if (!pageLoaded) {
            loadPerms();
            loadOrganizationsData();
            loadDestinationURlsData();
            loadDestinationTypesData();
        }
    }, [token, reloadDestinations, destinationFilter]);

    const handleAddDestinationPage = () => {
        navigate('/destinations/new');
    }

    const handleToggleDestinationDetailsModal = async (dest_id?: string) => {
        if (dest_id && token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            navigate(`/destinations/${dest_id}/configuration`, { replace: false });
        }
        setDestDetailsModal(!destDetailsModal);
        if (destDetailsModal) setReloadDestinations(true);
    }

    const handleGlobalSearch = (searchSubStr: string) => {
        setDestinationFilter(prev => ({ ...prev, search: searchSubStr }));
        setOffset(0);
        setReloadDestinations(true);
    }

    const handleFilterEndpoint = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, base_url__in: tags }));
        setOffset(0);
        setReloadDestinations(true);
    }

    const handleFilterType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, type__in: tags }));
        setOffset(0);
        setReloadDestinations(true);
    }

    const handleFilterOwner = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, owner__in: tags }));
        setOffset(0);
        setReloadDestinations(true);
    }

    const handleSortTable = (orderBy: string, sortDirection: boolean) => {
        const ordering = sortDirection ? orderBy : "-" + orderBy;
        setDestinationFilter(prev => ({ ...prev, ordering }));
        setReloadDestinations(true);
    }

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
        setReloadDestinations(true);
    }

    const toSingleValue = (tags: FilterTags[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const endpointSelectValue = toSingleValue(destinationFilter.base_url__in);
    const typeSelectValue = toSingleValue(destinationFilter.type__in);
    const ownerSelectValue = toSingleValue(destinationFilter.owner__in);

    return (
            <div>
                {destDetailsModal &&
                    <ManageDestinationDetails />
                }
                <DestinationsList
                    canAddDestination={canAddDestination}
                    reloadDestinations={reloadDestinations}
                    destinations={destinations}
                    destinationTypes={destinationTypes}
                    loadingDestinationTypes={loadingDestinationTypes}
                    destinationUrls={destinationUrls}
                    loadingDestinationUrls={loadingDestinationUrls}
                    organizations={organizations}
                    loadingOrganizations={loadingOrganizations}
                    onAddDestination={handleAddDestinationPage}
                    onViewDetails={handleToggleDestinationDetailsModal}
                    onChangeGlobalSearch={handleGlobalSearch}
                    globalSearchStr={destinationFilter.search}
                    onChangeEndpoint={handleFilterEndpoint}
                    endpointFilterValue={endpointSelectValue}
                    onChangeType={handleFilterType}
                    typeFilterValue={typeSelectValue}
                    onChangeOwner={handleFilterOwner}
                    ownerFilterValue={ownerSelectValue}
                    requestSort={handleSortTable}
                    onPageClicked={handlePageClick}
                    totalCount={totalCount}
                    pageLoaded={pageLoaded}
                    take={page_size}
                    offset={offset}
                />
            </div>
        );
};

export default DestinationsPage;
